import { FC, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PostHogProvider as Posthog, usePostHog } from 'posthog-js/react';
import { useGetMyInfoQuery } from 'src/hooks/useGetMyInfoQuery';
import { useTrackSignupCompleted } from 'src/hooks/useTrackSignupCompleted';

type PosthogProviderProps = {
  children: React.ReactNode;
};

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
// PostHog managed reverse proxy on our domain so ad blockers don't drop events
// https://posthog.com/docs/advanced/proxy/managed-reverse-proxy
const posthogApiHost = 'https://relay.ampersand.ai';
// keep toolbar and session-replay links pointing at the PostHog app, not the proxy
const posthogUiHost = 'https://us.posthog.com';

const excludeSet = new Set(['__clerk_handshake']);

const EnabledPostHogProvider: FC<PosthogProviderProps> = ({ children }) => {
  const { projectId } = useParams();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();

  // fires `Signup Completed` once per new account. safe to call here: the
  // <Posthog> provider below is a child, so its init effect runs first
  useTrackSignupCompleted();

  const memoizedSearchParams = useMemo(() => {
    const srchParams: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      if (excludeSet.has(key)) return;
      srchParams[key] = value;
    });

    return srchParams;
  }, [searchParams]);
  // reset posthog identity when projectId and builderInfo changes
  const { data: builderInfo } = useGetMyInfoQuery();

  useEffect(() => {
    if (!posthog._isIdentified() && builderInfo?.builder?.id && projectId && memoizedSearchParams) {
      const identity = { // identify the user with builder id
        idpProvider: builderInfo?.builder?.idpProvider,
        idpRef: builderInfo?.builder?.idpRef,
        primaryEmailAddress: builderInfo?.builder?.primaryEmail,
        orgId: builderInfo?.orgRole?.org?.id,
        builderId: builderInfo?.builder?.id,
        authType: 'user',
        searchParams: memoizedSearchParams,
      };

      posthog.identify(builderInfo?.builder?.id, identity);
    } 
  }, [projectId, builderInfo, memoizedSearchParams, posthog]);

  return (
    <Posthog
      apiKey={POSTHOG_API_KEY}
      options={{
        api_host: posthogApiHost,
        ui_host: posthogUiHost,
        person_profiles: 'identified_only',
      }}
    >
      {children}
    </Posthog>
  );
};

export const PostHogProvider: FC<PosthogProviderProps> = ({ children }) => (
  POSTHOG_API_KEY
    ? <EnabledPostHogProvider>{children}</EnabledPostHogProvider>
    : children
);
